/**
 * Purpose: Renders the top-down classroom desk grid with player tokens.
 * Owns: Grid layout based on ClassSize dimensions, individual desk rendering,
 *       player token placement, occupied/empty desk states, and seat numbers.
 * Notes: Desks are numbered left-to-right, top-to-bottom (back-left = seat 1,
 *        front-right = last seat). Back of classroom is at the top of the screen.
 *        Grid dimensions come from classConfig (CS-21). All colors use theme
 *        tokens (CS-22). No magic numbers — sizes defined as named constants (CS-20).
 */

import type { Player } from '../../types/game';
import type { ClassSize } from '../../config/classConfig';

// _______________________________________________
//              PROPS
// _______________________________________________

interface DeskGridProps {
  /** Grid dimensions and seat count for the current class */
  classSize: ClassSize;
  /** All players in the game session */
  players: Player[];
  /** The period index (0-based) this grid is displaying */
  periodIndex: number;
  /** ID of the player whose turn it currently is */
  activePlayerId: string;
  /** Seat number currently being animated (null if no animation) */
  animatingSeat: number | null;
}

interface DeskProps {
  /** Seat number (1-indexed) */
  seatNumber: number;
  /** Players occupying this desk */
  occupants: Player[];
  /** Whether any seat in the grid is past this one for the active player */
  isPassed: boolean;
  /** Whether this is the active player's current seat */
  isCurrentSeat: boolean;
  /** Whether this desk is being animated to right now */
  isAnimating: boolean;
  /** Desk width for responsive sizing */
  deskWidth: number;
}

interface PlayerTokenProps {
  /** The player to show */
  player: Player;
  /** Whether this player is the active turn-taker */
  isActive: boolean;
  /** Token size in pixels */
  size: number;
}

// _______________________________________________
//              LAYOUT CONSTANTS
// _______________________________________________
// Desk sizing adapts to grid column count so all grids fit on mobile (CS-20).

/** Maximum grid container width in pixels */
const GRID_MAX_WIDTH = 340;

/** Gap between desks in pixels */
const DESK_GAP = 8;

/** Minimum desk width */
const DESK_MIN_WIDTH = 48;

/** Desk aspect ratio (width:height) — slightly taller than wide for top-down look */
const DESK_HEIGHT_RATIO = 0.75;

/** Player token size relative to desk width */
const TOKEN_RATIO = 0.45;

/** Minimum token size */
const TOKEN_MIN_SIZE = 20;

/**
 * Calculate responsive desk width based on column count.
 * Ensures the grid fits within GRID_MAX_WIDTH on mobile screens.
 */
function calculateDeskWidth(cols: number): number {
  const totalGaps = (cols - 1) * DESK_GAP;
  const available = GRID_MAX_WIDTH - totalGaps;
  return Math.max(Math.floor(available / cols), DESK_MIN_WIDTH);
}

// _______________________________________________
//              DESK GRID COMPONENT
// _______________________________________________

export function DeskGrid({
  classSize,
  players,
  periodIndex,
  activePlayerId,
  animatingSeat,
}: DeskGridProps) {
  const [rows, cols] = classSize.grid;
  const totalSeats = classSize.seats;
  const deskWidth = calculateDeskWidth(cols);

  /** Find which players are sitting at a given seat in this period */
  function getOccupants(seatNumber: number): Player[] {
    return players.filter((p) => {
      const progress = p.classProgress[periodIndex];
      if (!progress) return false;
      /* Player is in this period and on this seat */
      return p.currentPeriod - 1 === periodIndex && progress.currentSeat === seatNumber;
    });
  }

  /** Check if the active player has already passed through a seat */
  function isSeatPassed(seatNumber: number): boolean {
    const activePlayer = players.find((p) => p.id === activePlayerId);
    if (!activePlayer) return false;

    const progress = activePlayer.classProgress[periodIndex];
    if (!progress) return false;

    /* If player is in a later period, all seats in this period are passed */
    if (activePlayer.currentPeriod - 1 > periodIndex) return true;

    /* If player is in this period, seats before current are passed */
    if (activePlayer.currentPeriod - 1 === periodIndex) {
      return seatNumber < progress.currentSeat;
    }

    return false;
  }

  /** Check if a seat is the active player's current seat */
  function isCurrentSeat(seatNumber: number): boolean {
    const activePlayer = players.find((p) => p.id === activePlayerId);
    if (!activePlayer) return false;
    if (activePlayer.currentPeriod - 1 !== periodIndex) return false;

    const progress = activePlayer.classProgress[periodIndex];
    return progress?.currentSeat === seatNumber;
  }

  /* Build grid rows. Seat numbering: left-to-right, top-to-bottom */
  const gridRows: number[][] = [];
  let seatCounter = 1;
  for (let r = 0; r < rows; r++) {
    const row: number[] = [];
    for (let c = 0; c < cols; c++) {
      if (seatCounter <= totalSeats) {
        row.push(seatCounter);
        seatCounter++;
      }
    }
    gridRows.push(row);
  }

  return (
    <div
      style={{
        ...gridStyles.container,
        maxWidth: `${GRID_MAX_WIDTH}px`,
      }}
      role="grid"
      aria-label={`Classroom with ${totalSeats} desks`}
    >
      {gridRows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          style={{
            ...gridStyles.row,
            gap: `${DESK_GAP}px`,
          }}
          role="row"
        >
          {row.map((seat) => (
            <Desk
              key={seat}
              seatNumber={seat}
              occupants={getOccupants(seat)}
              isPassed={isSeatPassed(seat)}
              isCurrentSeat={isCurrentSeat(seat)}
              isAnimating={animatingSeat === seat}
              deskWidth={deskWidth}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── Wood desk CSS gradient colors (CS-22: derived from --accent-wood token) ── */
const DESK_WOOD_BASE = '#7a5030';        // Base wood tone
const DESK_WOOD_LIGHT = '#8d6040';       // Lighter grain stripe
const DESK_WOOD_DARK = '#5c3a1e';        // Darker grain stripe
const DESK_WOOD_MUTED = '#6b5540';       // Faded/empty desk tone
const DESK_WOOD_MUTED_LIGHT = '#7a644e'; // Faded lighter stripe
const DESK_WOOD_MUTED_DARK = '#54432e';  // Faded darker stripe

/** CSS linear gradient simulating horizontal wood grain viewed from above. */
function woodGradient(base: string, light: string, dark: string): string {
  return `linear-gradient(
    90deg,
    ${dark} 0%, ${base} 8%, ${light} 18%, ${base} 28%,
    ${dark} 40%, ${light} 52%, ${base} 64%,
    ${dark} 76%, ${base} 88%, ${light} 95%, ${dark} 100%
  )`;
}

/* Pre-built gradients for each desk state to avoid re-computing every render. */
const WOOD_NORMAL = woodGradient(DESK_WOOD_BASE, DESK_WOOD_LIGHT, DESK_WOOD_DARK);
const WOOD_MUTED = woodGradient(DESK_WOOD_MUTED, DESK_WOOD_MUTED_LIGHT, DESK_WOOD_MUTED_DARK);

// _______________________________________________
//              DESK COMPONENT
// _______________________________________________
// Each desk is a top-down wooden rectangle with wood-grain CSS gradients.
// Shows seat number in top-left and player tokens on top.

function Desk({
  seatNumber,
  occupants,
  isPassed,
  isCurrentSeat,
  isAnimating,
  deskWidth,
}: DeskProps) {
  const deskHeight = Math.floor(deskWidth * DESK_HEIGHT_RATIO);
  const tokenSize = Math.max(Math.floor(deskWidth * TOKEN_RATIO), TOKEN_MIN_SIZE);
  const hasOccupants = occupants.length > 0;

  /* Determine desk visual state — wood tone, border highlight, and shadow */
  let background = WOOD_NORMAL;
  let borderColor = 'var(--accent-wood)';
  let shadow = 'inset 0 2px 4px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)';
  let numberOpacity = 0.5;
  let opacity = 1;

  if (isCurrentSeat || isAnimating) {
    /* Active player's desk — gold glow border */
    borderColor = 'var(--accent-gold)';
    shadow = '0 0 10px rgba(212, 168, 67, 0.45), inset 0 2px 4px rgba(0,0,0,0.25)';
    numberOpacity = 0.9;
  } else if (hasOccupants) {
    /* Another player's desk — their color as a thin border tint */
    borderColor = occupants[0].color;
    numberOpacity = 0.6;
  } else if (isPassed) {
    /* Already passed — muted wood, slightly transparent */
    background = WOOD_MUTED;
    borderColor = DESK_WOOD_MUTED_DARK;
    shadow = 'inset 0 2px 4px rgba(0,0,0,0.25)';
    numberOpacity = 0.4;
    opacity = 0.7;
  } else {
    /* Empty, not passed — natural wood, slightly faded to recede */
    background = WOOD_MUTED;
    opacity = 0.8;
    numberOpacity = 0.35;
  }

  return (
    <div
      className={isCurrentSeat ? 'chalk-pulse' : ''}
      style={{
        width: `${deskWidth}px`,
        height: `${deskHeight}px`,
        border: `2px solid ${borderColor}`,
        borderRadius: '5px',
        background,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        transition: 'all 0.3s ease',
        boxShadow: shadow,
        opacity,
      }}
      role="gridcell"
      aria-label={`Seat ${seatNumber}${hasOccupants ? `, occupied by ${occupants.map((p) => p.name).join(', ')}` : ''}`}
    >
      {/* Seat number — small and muted in top-left corner */}
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: '4px',
          fontSize: '0.55rem',
          fontFamily: 'var(--font-family)',
          color: 'var(--text-light)',
          opacity: numberOpacity,
          lineHeight: 1,
          textShadow: '0 1px 1px rgba(0,0,0,0.5)',
        }}
      >
        {seatNumber}
      </span>

      {/* Passed checkmark indicator */}
      {isPassed && !hasOccupants && (
        <span
          style={{
            fontSize: '0.7rem',
            opacity: 0.5,
            color: 'var(--correct)',
            textShadow: '0 1px 2px rgba(0,0,0,0.4)',
          }}
        >
          {'\u2713'}
        </span>
      )}

      {/* Player tokens — stack horizontally if multiple */}
      {hasOccupants && (
        <div
          style={{
            display: 'flex',
            gap: '2px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {occupants.map((player) => (
            <PlayerToken
              key={player.id}
              player={player}
              isActive={isCurrentSeat}
              size={tokenSize}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// _______________________________________________
//              PLAYER TOKEN COMPONENT
// _______________________________________________
// Colored circle with player initials, shown on occupied desks.

function PlayerToken({ player, isActive, size }: PlayerTokenProps) {
  return (
    <div
      className={isActive ? 'piece-advance' : ''}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: '50%',
        backgroundColor: player.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${Math.max(size * 0.45, 9)}px`,
        fontWeight: 700,
        fontFamily: 'var(--font-family)',
        color: 'var(--text-light)',
        border: '2px solid rgba(255, 255, 255, 0.35)',
        flexShrink: 0,
        transition: 'all 0.3s ease',
      }}
      aria-label={`${player.name}'s token`}
    >
      {player.name.charAt(0).toUpperCase()}
    </div>
  );
}

// _______________________________________________
//              GRID STYLES
// _______________________________________________

const gridStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${DESK_GAP}px`,
    margin: '0 auto',
    padding: '0.5rem',
  },
  row: {
    display: 'flex',
    justifyContent: 'center',
  },
};
